/**
 * SignInPage.js — Branded sign-in page wrapping Clerk's <SignIn> component.
 */

import React from 'react';
import { SignIn } from '@clerk/react';

const SignInPage = () => (
  <div style={s.root}>
    <style>{css}</style>

    {/* Left panel */}
    <div style={s.left}>
      <div style={s.leftInner}>
        <a href="https://testmyagent.io" style={s.logoLink}>
          <div style={s.logoMark}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="1" y="3" width="10" height="2" rx="1" fill="white"/>
              <rect x="1" y="7.5" width="7.5" height="2" rx="1" fill="rgba(255,255,255,0.55)"/>
              <rect x="1" y="12" width="5" height="2" rx="1" fill="rgba(255,255,255,0.3)"/>
              <circle cx="14.5" cy="4" r="3" fill="#00E5A0"/>
              <path d="M13 4L14.1 5.1L16.2 2.9" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span style={s.logoText}>test_my_<em style={s.logoEm}>agent</em></span>
        </a>

        <div>
          <h1 style={s.h1}>Welcome back.<br/><em style={s.h1Accent}>Your benchmarks are waiting.</em></h1>
          <p style={s.subtext}>Continue testing your Zendesk AI Agent with real ticket history, security scanning, and ROI projections — before your customers see it live.</p>
        </div>

        <div style={s.statsGrid}>
          {[
            ['5 metrics', 'Accuracy, Tone, Resolution, Efficiency, Groundedness'],
            ['73%', 'Of AI agents have prompt injection vulnerabilities'],
            ['v1.7.0', 'Current platform version — A/B comparison live'],
          ].map(([num, label]) => (
            <div key={num} style={s.statCard}>
              <div style={s.statNum}>{num}</div>
              <div style={s.statLabel}>{label}</div>
            </div>
          ))}
        </div>

        <ul style={s.featureList}>
          {['Human vs AI Benchmark with ROI projection','KB groundedness scoring — catch hallucinations','Prompt injection and security testing','A/B configuration comparison (Pro)'].map((f, i) => (
            <li key={i} style={s.featureItem}><span style={s.dot}/>{f}</li>
          ))}
        </ul>

        <div style={s.badge}><span style={s.badgeDot}/>PRIVATE BETA · LIMITED SPOTS AVAILABLE</div>
      </div>
    </div>

    {/* Right panel — Clerk SignIn */}
    <div style={s.right}>
      <div style={s.rightInner}>
        <div>
          <h2 style={s.formTitle}>Sign in to your account</h2>
          <p style={s.formSub}>Don't have an account? <a href="mailto:fernando@cxperiences.com?subject=Beta%20Access%20Request" style={s.link}>Request beta access</a></p>
        </div>
        <SignIn
          routing="hash"
          afterSignInUrl="/"
          appearance={clerkAppearance}
        />
      </div>
    </div>
  </div>
);

const clerkAppearance = {
  variables: {
    colorPrimary: '#6b46fa',
    colorBackground: '#0f0f1a',
    colorInputBackground: '#141420',
    colorInputText: '#eeebff',
    colorText: '#eeebff',
    colorTextSecondary: '#9e9bbf',
    colorNeutral: '#4e4b6a',
    colorDanger: '#ff4d6a',
    colorSuccess: '#00e5a0',
    borderRadius: '8px',
    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
    fontSize: '14px',
  },
  elements: {
    card: { background: 'transparent', boxShadow: 'none', border: 'none', padding: 0 },
    headerTitle: { display: 'none' },
    headerSubtitle: { display: 'none' },
    footer: { display: 'none' },
    socialButtonsBlockButton: {
      background: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.1)',
      color: '#eeebff', borderRadius: '8px',
    },
    dividerLine: { background: 'rgba(255,255,255,0.08)' },
    dividerText: { color: '#4e4b6a' },
    formFieldLabel: {
      color: '#9e9bbf', fontSize: '12px',
      fontFamily: "'IBM Plex Mono', monospace",
      letterSpacing: '0.06em', textTransform: 'uppercase',
    },
    formFieldInput: {
      background: '#141420', border: '1px solid rgba(255,255,255,0.1)',
      color: '#eeebff', borderRadius: '8px', fontSize: '14px',
    },
    formButtonPrimary: {
      background: 'linear-gradient(135deg, #6b46fa, #4f2fd4)',
      borderRadius: '8px',
      fontFamily: "'IBM Plex Mono', monospace",
      fontWeight: '500', letterSpacing: '0.02em', fontSize: '13px',
      boxShadow: '0 8px 24px rgba(107,70,250,0.3)',
    },
    footerActionLink: { color: '#9b7eff' },
    alertText: { color: '#ff4d6a' },
  },
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300&family=IBM+Plex+Mono:wght@400;500&display=swap');
  * { box-sizing: border-box; }
  .cl-card { background: transparent !important; box-shadow: none !important; border: none !important; padding: 0 !important; }
  .cl-headerTitle, .cl-headerSubtitle, .cl-footer, .cl-footerAction { display: none !important; }
  .cl-rootBox, .cl-signIn { width: 100% !important; }
  .cl-formButtonPrimary:hover { opacity: 0.88; }
  .cl-formFieldInput:focus { border-color: #6b46fa !important; outline: none !important; box-shadow: 0 0 0 2px rgba(107,70,250,0.2) !important; }
  .cl-internal-b3fm6y { display: none !important; }
  @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
  @media (max-width: 860px) { .auth-left { display: none !important; } }
`;

const s = {
  root: { display: 'flex', minHeight: '100vh', background: '#0a0a12', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" },
  left: { flex: '1 1 50%', background: 'linear-gradient(160deg, rgba(107,70,250,0.1) 0%, transparent 60%), #0a0a12', borderRight: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', overflow: 'hidden', className: 'auth-left' },
  leftInner: { maxWidth: '420px', width: '100%', display: 'flex', flexDirection: 'column', gap: '1.8rem', animation: 'fadeIn 0.5s ease both' },
  logoLink: { display: 'inline-flex', alignItems: 'center', gap: '10px', textDecoration: 'none' },
  logoMark: { width: '32px', height: '32px', background: '#6b46fa', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 16px rgba(107,70,250,0.4)' },
  logoText: { fontFamily: "'IBM Plex Mono', monospace", fontSize: '14px', fontWeight: '500', color: '#eeebff', letterSpacing: '0.02em' },
  logoEm: { fontStyle: 'normal', color: '#9b7eff' },
  h1: { fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', fontWeight: '600', lineHeight: '1.22', letterSpacing: '-0.02em', color: '#eeebff', margin: '0 0 0.8rem' },
  h1Accent: { color: '#9b7eff', fontStyle: 'italic', fontWeight: '300' },
  subtext: { fontSize: '14px', color: '#9e9bbf', lineHeight: '1.7', fontWeight: '300', margin: 0, paddingLeft: '14px', borderLeft: '2px solid rgba(107,70,250,0.3)' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' },
  statCard: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '14px 12px' },
  statNum: { fontFamily: "'IBM Plex Mono', monospace", fontSize: '18px', fontWeight: '500', color: '#00e5a0', letterSpacing: '-0.02em', marginBottom: '4px' },
  statLabel: { fontSize: '11px', color: '#4e4b6a', lineHeight: '1.4', fontWeight: '300' },
  featureList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' },
  featureItem: { display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: '#9e9bbf', fontWeight: '300', lineHeight: '1.5' },
  dot: { width: '5px', height: '5px', background: '#6b46fa', borderRadius: '50%', flexShrink: 0, marginTop: '5px', boxShadow: '0 0 6px rgba(107,70,250,0.5)' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: '8px', fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', color: '#9b7eff', letterSpacing: '0.08em', background: 'rgba(107,70,250,0.1)', border: '1px solid rgba(107,70,250,0.25)', padding: '6px 12px', borderRadius: '999px', width: 'fit-content' },
  badgeDot: { width: '5px', height: '5px', background: '#00e5a0', borderRadius: '50%', boxShadow: '0 0 6px rgba(0,229,160,0.7)', animation: 'pulse 2s infinite', display: 'inline-block' },
  right: { flex: '1 1 50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', background: '#0b0b16' },
  rightInner: { width: '100%', maxWidth: '420px', display: 'flex', flexDirection: 'column', gap: '1.8rem', animation: 'fadeIn 0.5s 0.1s ease both' },
  formTitle: { fontSize: '22px', fontWeight: '600', color: '#eeebff', letterSpacing: '-0.02em', margin: '0 0 6px' },
  formSub: { fontSize: '13px', color: '#9e9bbf', margin: 0, fontWeight: '300' },
  link: { color: '#9b7eff', textDecoration: 'none' },
};

export default SignInPage;
